import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { FREE_GUIDE_URL } from "@/data/content";

export default function FreeGuide() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Save the lead (re-use newsletter collection)
      await api.post("/newsletter", { email, name: name || null });
      // Trigger PDF download
      const a = document.createElement("a");
      a.href = FREE_GUIDE_URL;
      a.download = "Sano-5-Ways-to-Reduce-Stress.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Your guide is downloading. Check your downloads folder.");
      setEmail("");
      setName("");
    } catch {
      // Even on failure, let them have the guide
      window.location.href = FREE_GUIDE_URL;
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="py-24 lg:py-32 border-y"
      style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
      data-testid="free-guide-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div
            className="aspect-[8.5/11] border shadow-2xl flex items-center justify-center p-10 rounded-sm"
            style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
          >
            <div>
              <div className="eyebrow mb-3" style={{ color: "var(--sano-clay)" }}>FREE GUIDE</div>
              <div className="font-serif-display text-4xl leading-tight mb-2">5 Natural Ways</div>
              <div className="font-serif-display text-2xl leading-tight mb-2" style={{ color: "var(--sano-ink-2)" }}>
                to Reduce Stress and
              </div>
              <div className="font-serif-display text-2xl leading-tight" style={{ color: "var(--sano-ink-2)" }}>
                Regulate Your
              </div>
              <div className="font-serif-display text-3xl leading-tight mt-1" style={{ color: "var(--sano-olive)" }}>
                Nervous System
              </div>
              <div className="mt-8 text-xs uppercase tracking-widest" style={{ color: "var(--sano-ink-2)" }}>
                — Joannie Campuzano, L.Ac.
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <div className="eyebrow mb-3">Free download</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-5">
            5 Natural Ways to Reduce Stress and Regulate Your Nervous System
          </h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--sano-ink-2)" }}>
            Five evidence-informed practices drawn from ten years of clinical work and
            ancient Eastern medicine. Five minutes a day. No equipment. Yours to keep.
          </p>

          <form onSubmit={submit} className="space-y-3" data-testid="free-guide-form">
            <input
              type="text"
              placeholder="Your first name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="free-guide-name"
            />
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="free-guide-email"
            />
            <button
              type="submit"
              disabled={loading || !email}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
              style={{ background: "var(--sano-olive)" }}
              data-testid="free-guide-submit"
            >
              <Download size={14} strokeWidth={1.5} />
              {loading ? "Preparing…" : "Send me the guide"}
            </button>
          </form>
          <p className="mt-3 text-xs" style={{ color: "var(--sano-ink-2)" }}>
            Instant PDF download. No spam. Unsubscribe any time.
          </p>
        </div>
      </div>
    </section>
  );
}
